import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { gql, useLazyQuery } from "@apollo/client";
import { signIn } from "../../utils/authHelpers";
import GuestModeModal from "../../components/GuestModeModal";
import {
  createAndStorePkce,
  OAUTH_STATE_KEY,
  PKCE_VERIFIER_KEY,
} from "../../utils/pkce";
import { X, ChevronDown, ChevronUp } from "lucide-react";

const clean = (v) => (v || "").replace(/^https?:\/\//, "").replace(/\/+$/, "");
const DEFAULT_COGNITO_HOST =
  "pounce-dev-colabo.auth.us-east-1.amazoncognito.com";

const RAW_COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN;
let COGNITO_HOST = clean(RAW_COGNITO_DOMAIN);
if (!COGNITO_HOST || COGNITO_HOST.startsWith(".") || COGNITO_HOST.includes("..")) {
  console.warn("[Cognito] Bad/empty domain from env:", RAW_COGNITO_DOMAIN, "→ using DEFAULT");
  COGNITO_HOST = DEFAULT_COGNITO_HOST;
}

const REDIRECT_URI = `${window.location.origin}/callback`;
const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID;

// Shared OAuth scopes (Cognito app client scopes)
const COGNITO_SCOPES = "openid email profile";

console.log("[Cognito] DOMAIN raw/clean:", RAW_COGNITO_DOMAIN, "→", COGNITO_HOST);
console.log("[Cognito] CLIENT_ID:", COGNITO_CLIENT_ID);
console.log("[Cognito] REDIRECT_URI:", REDIRECT_URI);

const GUEST_LOGIN = gql`
  query GuestLogin {
    guestLogin {
      success
      sessionId
      token
      expiresAt
    }
  }
`;

const HAS_SEEN_TUTORIAL = gql`
  query HasSeenTutorial {
    hasSeenTutorial
  }
`;

const Welcome = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();

  const [showGuestPopup, setShowGuestPopup] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [showSSO, setShowSSO] = useState(false);
  const [showMobileIntro, setShowMobileIntro] = useState(false); 

  const [hasSeenTutorial] = useLazyQuery(HAS_SEEN_TUTORIAL, {
    fetchPolicy: "no-cache",
    skip: true,
  });

  const [guestLogin] = useLazyQuery(GUEST_LOGIN, {
    fetchPolicy: "no-cache",
    skip: true,
  });

  useEffect(() => {
    localStorage.removeItem("idToken");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("sessionID");
    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    setIsAuthenticated(false);
  }, [setIsAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    try {
      await signIn(email, password);
      setIsAuthenticated(true);

      const { data } = await hasSeenTutorial();
      if (data.hasSeenTutorial === true) {
        navigate("/home");
      } else {
        navigate("/howto");
      }
    } catch (err) {
      setLoginError(err.message || "Failed to log in. Please try again.");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleForgotPasswordRedirect = () => {
    const url = `https://${COGNITO_HOST}/login?client_id=${encodeURIComponent(
      COGNITO_CLIENT_ID || ""
    )}&response_type=code&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&forgotPassword=true`;
    console.log("[Cognito] Forgot Password URL:", url);
    window.location.assign(url);
  };

  const launchIdp = async (identityProvider) => {
    try {
      // remember which IdP the user chose (used if we need to restart)
      sessionStorage.setItem("lastIdp", identityProvider);

      const { codeChallenge, state } = await createAndStorePkce();
      const params = new URLSearchParams({
        identity_provider: identityProvider, // "Google" or "Facebook"
        redirect_uri: REDIRECT_URI,
        response_type: "code",
        client_id: COGNITO_CLIENT_ID || "",
        scope: COGNITO_SCOPES,
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
        prompt: "login select_account",
        state,
      });
      const url = `https://${COGNITO_HOST}/oauth2/authorize?${params.toString()}`;
      console.log(`[Cognito] Authorize URL (${identityProvider}):`, url);
      window.location.assign(url);
    } catch (e) {
      console.error("PKCE setup failed:", e);
    }
  };

  const handleGoogleSSO = () => launchIdp("Google");
  const handleFacebookSSO = () => launchIdp("Facebook");

  const handleGuest = async () => {
    try {
      const { data } = await guestLogin();
      const { success, token, sessionId } = data.guestLogin;
      if (success) {
        localStorage.setItem("accessToken", token);
        localStorage.setItem("sessionID", sessionId);
        setIsAuthenticated(true);
        setShowGuestPopup(true);
      } else {
        console.error("Guest login failed");
      }
    } catch (err) {
      console.error("Error during guest login:", err);
    }
  };

  const handleGuestPopupContinue = () => {
    localStorage.setItem("guestPopupAck", "true");
    setShowGuestPopup(false);
    navigate("/howto");
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-[#232222] text-white relative px-4">
      {/* Desktop heading + Subtitle */}
      <div className="w-full max-w-3xl text-center mb-8 z-10 hidden lg:block">
        <h1 className="text-[48px] lg:text-[56px] font-extrabold font-[Montserrat] leading-[1.1] tracking-[-0.02em] mb-4">
          Welcome to <span className="text-[#CBDF52]">Pounce!</span>
        </h1>
        <p className="text-[#cccccc] text-lg leading-relaxed">
          A citizen science project where you can help researchers study puppy behavior
        </p>
      </div>

      <div className="w-full max-w-md mx-auto lg:hidden z-10">
      <div className="w-full bg-[#232222] px-7 py-8">
      {/* Title */}
          <h1 className="text-[40px] font-extrabold font-[Montserrat] leading-[1.1] tracking-[-0.02em] mb-2 text-center">
            Welcome to <span className="text-[#CBDF52]">Pounce!</span>
          </h1>
          <p className="text-[#cccccc] text-ml leading-relaxed mb-6 max-w-xs text-center">
            A citizen science project where you can help researchers study puppy behavior
          </p>

          <button
            type="button"
            onClick={() => setShowMobileIntro((prev) => !prev)}
            className="w-full flex flex-col items-center justify-center mb-4 text-xs text-gray-300"
          >
            {showMobileIntro ? (
              <ChevronUp size={16} className="mb-1" />
            ) : (
              <ChevronDown size={16} className="mb-1" />
            )}
            <span className="tracking-wide">Watch introduction</span>
          </button>

          {showMobileIntro && (
            <div className="mb-6">
              <div className="bg-[#111111] rounded-2xl border border-[#2b2b2b] overflow-hidden">
                <div className="relative w-full pb-[54.25%]">
                  <iframe
                    title="Pounce Welcome Video"
                    src="https://player.mux.com/CztgvQ9O01NLby4Rb4S9cvzpevEMOVkBQX00oa005G88mA?metadata-video-title=Pounce+Welcome+Video&video-title=Pounce+Welcome+Video&thumbnail-time=1"
                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full border-0"
                  />
                </div>
              </div>
            </div>
          )}

          <h2 className="text-base font-semibold mb-9">Sign in</h2>

          <form onSubmit={handleLogin} className="space-y-4">
            {loginError && (
              <p className="text-red-400 text-sm">{loginError}</p>
            )}

            <div className="flex flex-col text-left">
              <label className="text-[#65DEF6] mb-1 text-xs">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full h-[44px] px-3 bg-[#2F2D2D] rounded-lg border border-[#3a3a3a] text-white text-sm"
              />
            </div>

            <div className="flex flex-col text-left">
              <label className="text-[#65DEF6] mb-1 text-xs">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full h-[44px] px-3 bg-[#2F2D2D] rounded-lg border border-[#3a3a3a] text-white text-sm"
              />
              <p
                className="text-[11px] text-[#CBDF52] hover:underline cursor-pointer mt-1 self-end"
                onClick={handleForgotPasswordRedirect}
              >
                Forgot password?
              </p>
            </div>

            <button
              type="submit"
              disabled={loginLoading}
              className="w-full h-[45px] bg-[#CBDF52] text-black rounded-lg text-sm font-semibold hover:bg-green-500 transition disabled:opacity-50"
            >
              {loginLoading ? "Logging in..." : "Log in"}
            </button>
          </form>

          <div className="flex items-center my-4">
            <div className="flex-grow border-t border-gray-600" />
            <span className="mx-2 text-[11px] text-gray-500">or</span>
            <div className="flex-grow border-t border-gray-600" />
          </div>

          <div className="flex flex-col space-y-2">
            <button
              onClick={() => setShowSSO(true)}
              className="w-full h-[45px] bg-white text-black rounded-lg font-semibold hover:bg-gray-200 transition disabled:opacity-50"
              // className="w-full h-[45px] rounded-lg border border-white text-white text-sm font-semibold bg-transparent hover:bg-white hover:text-black transition"
            >
              Sign up with SSO
            </button>

            {/* <button
              onClick={handleGuest}
              className="w-full h-[45px] bg-white text-black rounded-lg text-sm font-semibold hover:bg-gray-200 transition"
            >
              Continue as Guest
            </button> */}
          </div>

          {/* Create account text */}
          <p className="mt-6 text-[17px] text-gray-300 text-center">
            Don’t have an account?{" "}
            <span
              className="text-[#CBDF52] hover:underline cursor-pointer font-semibold"
              onClick={() => navigate("/create-account")}
            >
              Create Account
            </span>{" "}
            instead!
          </p>
        </div>
      </div>

      <div className="w-full max-w-6xl hidden lg:flex lg:flex-row items-start gap-12 z-10">

        <div className="flex-1 max-w-[760px]">
          <div className="max-w-[720px] pt-12">
            <div className="bg-[#111111] rounded-2xl border border-[#2b2b2b] shadow-[0_20px_40px_rgba(0,0,0,0.6)]">
              <div className="relative w-full pb-[54.25%] overflow-hidden rounded-2xl">
                <iframe
                  title="Pounce Welcome Video"
                  src="https://player.mux.com/CztgvQ9O01NLby4Rb4S9cvzpevEMOVkBQX00oa005G88mA?metadata-video-title=Pounce+Welcome+Video&video-title=Pounce+Welcome+Video&thumbnail-time=1"
                  allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full border-0"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 max-w-md w-full bg-[#232222]/60 rounded-2xl p-8 text-center space-y-4">
          <form onSubmit={handleLogin} className="space-y-4">
            {loginError && <p className="text-red-400">{loginError}</p>}

            <div className="flex flex-col text-left">
              <label className="text-[#65DEF6] mb-1">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full p-3 bg-gray-800 rounded-lg border border-gray-600 text-white"
              />
            </div>

            <div className="flex flex-col text-left">
              <label className="text-[#65DEF6] mb-1">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full p-3 bg-gray-800 rounded-lg border border-gray-600 text-white"
              />
              <p
                className="text-sm text-[#CBDF52] hover:underline cursor-pointer mt-1 self-end"
                onClick={handleForgotPasswordRedirect}
              >
                Forgot password?
              </p>
            </div>

            <button
              type="submit"
              disabled={loginLoading}
              className="w-full h-[45px] bg-[#CBDF52] text-black rounded-lg font-semibold hover:bg_GREEN-500 transition disabled:opacity-50"
            >
              {loginLoading ? "Logging in..." : "Log in"}
            </button>
          </form>

          <div className="flex items-center my-2">
            <div className="flex-grow border-t border-gray-600" />
            <span className="mx-2 text-gray-500">or</span>
            <div className="flex-grow border-t border-gray-600" />
          </div>

          <div className="flex flex-col space-y-2">
            <button
              onClick={() => setShowSSO(true)}
              className="w-full h-[45px] bg-white text-black rounded-lg font-semibold hover:bg-gray-200 transition disabled:opacity-50"
              // className="w-full h-[45px] rounded-lg border border-white text-white text-sm font-semibold bg-transparent hover:bg-white hover:text-black transition"
            >
              Sign up with SSO
            </button>

            {/* <button
              onClick={handleGuest}
              className="w-full h-[45px] bg-white text-black rounded-lg text-sm font-semibold hover:bg-gray-200 transition"
            >
              Continue as Guest
            </button>             */}
          </div>

          <p className="mt-4 text-white-400">
            Don’t have an account?{" "}
            <span
              className="text-[#CBDF52] hover:underline cursor-pointer font-semibold"
              onClick={() => navigate("/create-account")}
            >
              Create Account
            </span>{" "}
            instead!
          </p>
        </div>
      </div>

      <img
        src="/assets/images/puppy-2.png"
        alt="Dog"
        className="hidden lg:block absolute w-[230px] h-[190px] top-[80px] right-[50px] opacity-90"
      />
      <img
        src="/assets/images/puppy-1.png"
        alt="Second Dog"
        className="hidden lg:block absolute w-[228px] h-[170px] bottom-[50px] left-[10px] opacity-90"
      />

      {showSSO && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="relative bg-white rounded-2xl w-[400px] p-6 shadow-lg">
            <button
              onClick={() => setShowSSO(false)}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
            >
              <X size={24} />
            </button>
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Sign in using SSO
            </h2>

            <div className="flex flex-col items-center gap-3">
              {/* Google */}
              <button
                onClick={() => launchIdp("Google")}
                className="flex items-center gap-3 w-full max-w-[300px] p-3 rounded-lg shadow hover:shadow-md border border-gray-200 bg-white"
              >
                <img
                  src="/assets/images/google.png"
                  alt="Google"
                  className="w-6 h-6"
                />
                <span className="text-lg font-medium text-gray-800">
                  Google
                </span>
              </button>

              {/* Facebook */}
              {/* <button
                onClick={() => launchIdp("Facebook")}
                className="flex items-center gap-3 w-full max-w-[300px] p-3 rounded-lg shadow hover:shadow-md border border-gray-200 bg-white"
              >
                <img src="/assets/images/Facebook.png" alt="Facebook" className="w-6 h-6" />
                <span className="text-lg font-medium text-gray-800">Facebook</span>
              </button> */}
            </div>

            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  setShowSSO(false);
                  navigate("/login");
                }}
                className="text-sm text-gray-600 underline hover:text-gray-800"
              >
                Sign in using email instead
              </button>
            </div>
          </div>
        </div>
      )}

      {showGuestPopup && (
        <GuestModeModal
          dismissible
          onContinue={handleGuestPopupContinue}
          onClose={() => setShowGuestPopup(false)}
        />
      )}
    </div>
  );
};

export default Welcome;
